import { useCallback, useEffect, useRef, useState } from 'react'
import { useAccessContext } from '../../../context/AccessContext'
import { useAuth } from '../../../context/AuthContext'
import { getApiAuthToken } from '../../../services/api'
import { workerRequestFailure, type WorkerRequestFailure } from '../../../services/workerFailure'
import type {
  JobShift,
  ShiftApplication,
  WorkerAvailabilityWindow,
  WorkerProfileDetail,
  WorkerProfileDocument,
  WorkerUnavailabilityWindow,
} from '../../../types/access'
import {
  workerService,
  type WorkerAssignment,
  type WorkerDecision,
  type WorkerDecisionAppeal,
  type WorkerLocationPrivacyStatus,
  type WorkerMessageThread,
  type WorkerNotification,
  type WorkerSkillProgression,
  type WorkerTimesheet,
} from '../../../services/worker'

/**
 * Shared worker data loader for both worker shells.
 *
 * The phone shell and the wide-layout shell are two presentations of ONE
 * client: they load from the same canonical worker APIs, in the same order,
 * with the same partial-failure behavior. No worker-specific business logic
 * lives here — every capability flag on an assignment is backend-provided.
 */

export type WorkerWorkspaceData = {
  today: WorkerAssignment[]
  upcoming: WorkerAssignment[]
  history: WorkerAssignment[]
  punch: any
  shifts: JobShift[]
  recommended: JobShift[]
  applications: ShiftApplication[]
  availability: WorkerAvailabilityWindow[]
  unavailability: WorkerUnavailabilityWindow[]
  profile: WorkerProfileDetail | null
  documents: WorkerProfileDocument[]
  timesheets: WorkerTimesheet[]
  threads: WorkerMessageThread[]
  notifications: WorkerNotification[]
  skills: Array<Record<string, any>>
  progression: WorkerSkillProgression[]
  decisions: WorkerDecision[]
  appeals: WorkerDecisionAppeal[]
  locationPrivacy: WorkerLocationPrivacyStatus | null
}

export const emptyWorkerWorkspaceData: WorkerWorkspaceData = {
  today: [], upcoming: [], history: [], punch: null, shifts: [], recommended: [], applications: [],
  availability: [], unavailability: [], profile: null, documents: [], timesheets: [], threads: [],
  notifications: [], skills: [], progression: [], decisions: [], appeals: [], locationPrivacy: null,
}

export const friendlyWorkerError = (error: unknown) => workerRequestFailure(error).message
export type WorkerDataKey = keyof WorkerWorkspaceData
export const workerDataKeys = Object.keys(emptyWorkerWorkspaceData) as WorkerDataKey[]
export type WorkerSectionFailures = Partial<Record<WorkerDataKey, WorkerRequestFailure>>

export interface WorkerWorkspaceState {
  data: WorkerWorkspaceData
  failures: WorkerSectionFailures
  authenticationIssue: WorkerRequestFailure | null
  recoveryCodesPending: boolean
  loading: boolean
  error: string
  notice: string
  busy: string
  setError: (message: string) => void
  setNotice: (message: string) => void
  load: () => Promise<void>
  run: (key: string, action: () => Promise<unknown>, success: string) => Promise<void>
}

export function useWorkerWorkspaceData(): WorkerWorkspaceState {
  const { selectedTenantId, accessContext } = useAccessContext()
  const { user, hasPendingMfaRecoveryCodes } = useAuth()
  const pendingCodes = useRef(hasPendingMfaRecoveryCodes)
  pendingCodes.current = hasPendingMfaRecoveryCodes
  const scope = `${user?.id || ''}:${selectedTenantId || accessContext?.tenantId || ''}`
  const currentScope = useRef(scope)
  currentScope.current = scope
  const generation = useRef(0)
  const mounted = useRef(true)
  const [loadedScope, setLoadedScope] = useState(scope)
  const [failures, setFailures] = useState<WorkerSectionFailures>({})
  const [authenticationIssue, setAuthenticationIssue] = useState<WorkerRequestFailure | null>(null)
  const [data, setData] = useState<WorkerWorkspaceData>(emptyWorkerWorkspaceData)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState('')

  const load = useCallback(async () => {
    if (!mounted.current || currentScope.current !== scope) return
    if (pendingCodes.current()) { setLoadedScope(scope); setLoading(false); return }
    const requestGeneration = ++generation.current
    const token = getApiAuthToken()
    setError('')
    const settled = await Promise.allSettled([
      workerService.getTodayAssignments(), workerService.getUpcomingAssignments(), workerService.getHistory(),
      workerService.getPunchStatus(), workerService.getOpenShifts(), workerService.getRecommendedShifts(),
      workerService.getApplications(), workerService.getAvailability(), workerService.getUnavailability(),
      workerService.getProfile(), workerService.getDocuments(), workerService.getTimesheets(),
      workerService.getThreads(), workerService.getNotifications(), workerService.getSkills(),
      workerService.getSkillProgression(), workerService.getDecisions(), workerService.getDecisionAppeals(),
      workerService.getLocationPrivacyStatus(),
    ])
    if (!mounted.current || requestGeneration !== generation.current || currentScope.current !== scope || token !== getApiAuthToken()) return
    setLoadedScope(scope)
    const value = <T,>(index: number, fallback: T): T =>
      settled[index].status === 'fulfilled' ? (settled[index] as PromiseFulfilledResult<T>).value : fallback
    setData({
      today: value(0, []), upcoming: value(1, []), history: value(2, []), punch: value(3, null),
      shifts: value(4, []), recommended: value(5, []), applications: value(6, []), availability: value(7, []),
      unavailability: value(8, []), profile: value(9, null), documents: value(10, []), timesheets: value(11, []),
      threads: value(12, []), notifications: value(13, []), skills: value(14, []), progression: value(15, []),
      decisions: value(16, []), appeals: value(17, []), locationPrivacy: value(18, null),
    })
    const nextFailures: WorkerSectionFailures = {}
    settled.forEach((result, index) => {
      if (result.status === 'rejected') nextFailures[workerDataKeys[index]] = workerRequestFailure(result.reason)
    })
    setFailures(nextFailures)
    const allFailures = Object.values(nextFailures)
    const security = allFailures.find(failure => failure.recovery === 'signin')
      || allFailures.find(failure => failure.recovery === 'enroll') || null
    setAuthenticationIssue(security)
    setError((security || allFailures[0])?.message || '')
    setLoading(false)
  }, [scope])

  useEffect(() => {
    mounted.current = true
    setLoading(true)
    setData(emptyWorkerWorkspaceData)
    setFailures({})
    setAuthenticationIssue(null)
    setNotice('')
    void load()
    return () => { mounted.current = false; generation.current++ }
  }, [load, scope])

  // A second action while one is already in flight is ignored, matching the
  // existing wide-layout behavior. The ref (not `busy`) is the guard so rapid
  // double taps cannot both pass before React re-renders.
  const inFlight = useRef('')

  const run = useCallback(async (key: string, action: () => Promise<unknown>, success: string) => {
    if (!mounted.current || currentScope.current !== scope || inFlight.current || pendingCodes.current()) return
    const actionScope = scope
    const token = getApiAuthToken()
    inFlight.current = key
    setBusy(key)
    setError('')
    setNotice('')
    try {
      await action()
      if (!mounted.current || currentScope.current !== actionScope || token !== getApiAuthToken()) return
      setNotice(success)
      await load()
    } catch (err) {
      if (!mounted.current || currentScope.current !== actionScope || token !== getApiAuthToken()) return
      const failure = workerRequestFailure(err)
      setError(failure.message)
      if (failure.recovery !== 'retry') setAuthenticationIssue(failure)
    } finally {
      inFlight.current = ''
      setBusy('')
    }
  }, [load, scope])

  const current = loadedScope === scope
  return { data: current ? data : emptyWorkerWorkspaceData, failures: current ? failures : {}, authenticationIssue: current ? authenticationIssue : null, recoveryCodesPending: pendingCodes.current(), loading: loading || !current, error: current ? error : '', notice: current ? notice : '', busy, setError, setNotice, load, run }
}
